import type { ComponentProps, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

export interface PageTab {
  value: string;
  label: string;
  disabled?: boolean;
}

type PageTabsProps = {
  tabs: PageTab[];
  children: ReactNode;
  className?: string;
} & (
  | { value: string; onValueChange: (value: string) => void; defaultValue?: never }
  | { defaultValue: string; value?: never; onValueChange?: never }
);

function scrollActiveTabIntoView(container: HTMLDivElement | null) {
  if (!container) return;
  const activeTab = container.querySelector<HTMLElement>('[data-active]');
  activeTab?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
}

export function PageTabs({ tabs, children, className, value, defaultValue, onValueChange }: PageTabsProps) {
  const isURLMode = value !== undefined;
  const scrollRef = useRef<HTMLDivElement>(null);

  // URL mode: scroll on initial render and when value changes via back/forward navigation
  useEffect(() => {
    scrollActiveTabIntoView(scrollRef.current);
  }, [value]);

  function handleValueChange(newValue: string) {
    onValueChange?.(newValue);
    // In-page mode: Base UI sets data-active after this callback, so defer scroll one frame
    if (!isURLMode) {
      requestAnimationFrame(() => scrollActiveTabIntoView(scrollRef.current));
    }
  }

  return (
    <Tabs
      value={value}
      defaultValue={defaultValue}
      onValueChange={handleValueChange}
      className={cn('w-full', className)}
    >
      <div
        ref={scrollRef}
        className="overflow-x-auto border-b border-border [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      >
        <TabsList className="flex min-w-max h-auto bg-transparent p-0 rounded-none justify-start">
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              disabled={tab.disabled}
              className="shrink-0 whitespace-nowrap rounded-none py-3 px-4 mb-[-1px] border-b-2 border-transparent text-muted-foreground data-active:border-primary data-active:text-foreground data-active:bg-transparent data-active:shadow-none"
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>
      {isURLMode ? (
        <TabsContent value={value} className="mt-6">
          {children}
        </TabsContent>
      ) : (
        children
      )}
    </Tabs>
  );
}

export function PageTabPanel({ className, ...props }: ComponentProps<typeof TabsContent>) {
  return <TabsContent className={cn('mt-6', className)} {...props} />;
}
